
import { login } from '@/api/admin'
import Vue, { PropType } from 'vue'

// 声明天地图全局变量
declare global {
	interface Window {
		T: any
		Tdta: any
		TianDiTu: any // 可能的其他命名空间
	}
}

// 天地图key
const TDT_KEY = '29cb920a0dc1f34e109fd2b9c11172ae'

// 动态加载天地图API
const loadTDTMap = (key: string): Promise<any> => {
	return new Promise((resolve, reject) => {
		// 检查是否已加载
		if (window.T) {
			resolve(window.T)
			return
		}

		// 设置加载超时
		const timeoutTimer = setTimeout(() => {
			reject(new Error('天地图API加载超时'))
		}, 10000)

		// 创建script标签加载天地图API核心
		const script = document.createElement('script')
		script.type = 'text/javascript'
		// 尝试不同版本的API
		script.src = `https://api.tianditu.gov.cn/api?v=4.0&tk=${key}`

		script.onload = () => {
			clearTimeout(timeoutTimer)

			// 尝试多种可能的全局对象
			const mapObject = window.T || window.Tdta || window.TianDiTu
			if (!mapObject) {
				reject(new Error('未找到天地图全局对象'))
				return
			}

			// 尝试加载所需模块
			if (mapObject.load) {
				// 尝试不同的模块名称组合
				const moduleSets = [
					['search', 'geocoder'],
					['service.Search', 'service.Geocoder'],
					['T.service.Search', 'T.service.Geocoder'],
					['SearchService', 'GeocoderService'],
				]

				// 尝试第一种可用的模块组合
				const tryLoadModules = (index: number) => {
					if (index >= moduleSets.length) {
						// 所有模块组合都尝试过，仍加载核心
						resolve(mapObject)
						return
					}

					mapObject.load(moduleSets[index], (success: boolean) => {
						if (success) {
							resolve(mapObject)
						} else {
							tryLoadModules(index + 1)
						}
					})
				}

				tryLoadModules(0)
			} else {
				// 不支持模块化加载的兼容模式
				resolve(mapObject)
			}
		}

		script.onerror = () => {
			clearTimeout(timeoutTimer)
			reject(new Error('天地图API加载失败，请检查网络或密钥'))
		}

		document.head.appendChild(script)
	})
}

/** 获取默认经纬度（北京） */
export const getDefaultLngLat = () => ({ lng: 108.94272, lat: 34.2625 })

export default Vue.extend({
	props: {
		tdtKey: {
			type: String,
			default: TDT_KEY,
		},
		lng: {
			type: [Number, String],
			default: 0,
		},
		lat: {
			type: [Number, String],
			default: 0,
		},
		lngs: {
			type: [Number, String],
			default: 0,
		},
		lats: {
			type: [Number, String],
			default: 0,
		},
		size: {
			type: Object as PropType<{ width: string; height: string }>,
			default() {
				return { width: '100%', height: '500px' }
			},
		},
	},
	data() {
		return {
			handleSearch: false,
			searchInput: '',
			$map: null as any | null,
			$marker: null as any | null,
			searchResults: [] as Array<{ name: string; address: string; lng: number; lat: number; zoom?: number }>,
			searchService: null as any | null,
			geocoder: null as any | null,
			searchTimer: null as number | null,
			isLoading: false,
			isSearchReady: false,
			hasSearchError: false,
			searchErrorMsg: '',
			// 新增：可视范围坐标
			visibleBounds: null as any | null,
		}
	},
	methods: {
		// 在methods中添加
		resetMap() {
			// 重置地图状态
			if (this.$map) {
				// 执行必要的清理操作
				this.$map.removeEventListener('click')
				this.$map.removeEventListener('moveend')
				this.$map = null
			}
			// 重新初始化地图
			this.initMap()
		},
		/** 加载地图方法 */
		loadMap(options: any = {}) {
			return loadTDTMap(this.tdtKey).then((T) => {
				this.$emit('load', T)
				return T
			})
		},

		setMapCenter(lng: number, lat: number, zoom?: number) {
			if (!this.$map) {
				console.error('setMapCenter失败：地图实例不存在')
				return false
			}

			// 1. 校验经纬度是否为有效数字
			if (isNaN(lng) || isNaN(lat) || lng < -180 || lng > 180 || lat < -90 || lat > 90) {
				console.error('setMapCenter失败：无效经纬度', { lng, lat })
				return false
			}

			// 2. 创建坐标实例并校验有效性
			let point: any
			try {
				point = new window.T.LngLat(lng, lat)
				// 天地图LngLat实例通常有lng/lat属性，无则视为无效
				if (!point || (typeof point.lng !== 'function' && typeof point.lat !== 'function')) {
					console.error('setMapCenter失败：创建LngLat实例无效', point)
					return false
				}
			} catch (err) {
				console.error('setMapCenter失败：创建LngLat实例报错', err)
				return false
			}

			// 3. 校验缩放级别（可选）
			const validZoom = zoom && !isNaN(zoom) && zoom >= 1 && zoom <= 18 ? zoom : this.$map.getZoom()

			try {
				// 优先使用天地图标准方法（4.0版本推荐setCenter+setZoom）
				if (typeof this.$map.setCenter === 'function' && typeof this.$map.setZoom === 'function') {
					this.$map.setCenter(point) // 先设中心
					this.$map.setZoom(validZoom) // 再设缩放
					console.log('setMapCenter成功（标准方法）', { lng, lat, zoom: validZoom })
					return true
				}
				// 兼容旧版本方法（如setView）
				else if (typeof this.$map.setView === 'function') {
					this.$map.setView(point, validZoom)
					console.log('setMapCenter成功（兼容setView）', { lng, lat, zoom: validZoom })
					return true
				}
				// 兼容平移方法（panTo适合平滑移动）
				else if (typeof this.$map.panTo === 'function') {
					this.$map.panTo(point)
					this.$map.setZoom(validZoom)
					console.log('setMapCenter成功（兼容panTo）', { lng, lat, zoom: validZoom })
					return true
				} else {
					console.error('setMapCenter失败：未找到可用的地图方法')
					return false
				}
			} catch (err) {
				console.error('setMapCenter失败：方法调用报错', err)
				return false
			}
		},

		/** 使用REST API进行搜索（备选方案） */
		restApiSearch(keyword: string) {
			return new Promise((resolve, reject) => {
				const url = `http://api.tianditu.gov.cn/v2/search?postStr={"keyWord":"${encodeURIComponent(keyword)}","mapBound":"${
					this.visibleBounds
				}","level":12, "queryType": 1,"start": 0,"count": 10,"show": 1 }&type=query&tk=${this.tdtKey}`
				fetch(url)
					.then((response) => response.json())
					.then((result) => {
						console.log('天地图API返回结果:', result)

						if (result.status.infocode === 1000 && result) {
							resolve({ pois: result })
						} else {
							// reject(new Error(`搜索失败: ${result.message || '未知错误'}`));
						}
					})
					.catch((err) => {
						console.log('天地图API搜索报错:', err)
						reject(err)
					})
			})
		},

		/** 更新可视范围坐标 */
		updateVisibleBounds() {
			if (!this.$map) return

			// 获取地图边界
			const bounds = this.$map.getBounds()

			// 解析边界坐标
			this.visibleBounds = []
			// this.visibleBounds = [
			//     bounds.getNorthEast().lng,
			//     bounds.getNorthEast().lat,
			//     bounds.getSouthWest().lng,
			//     bounds.getSouthWest().lat].join(',')
			this.visibleBounds = [-180, -90, 180, 90]

			// 触发自定义事件，将范围坐标暴露给父组件
			console.log('更新可视范围坐标:', this.visibleBounds)

			// this.$emit('visible-bounds-change', this.visibleBounds);
		},

		initMap() {
			this.isLoading = true
			this.loadMap()
				.then((T) => {
					// 输出API结构供调试
					console.log('天地图API结构:', T)

					// 获取初始经纬度
					const defaultLngLat = getDefaultLngLat()
					let initLng = Number(this.lngs) ? Number(this.lngs) : defaultLngLat.lng
					let initLat = Number(this.lats) ? Number(this.lats) : defaultLngLat.lat
					console.log('传过来的', this.lngs, this.lats)
					console.log(defaultLngLat)

					console.log(initLng, initLat)

					console.log('初始经纬度:', Number(this.lng), Number(this.lat))
					console.log(defaultLngLat)
					console.log(initLng, initLat)

					// 验证经纬度有效性
					if (isNaN(initLng) || isNaN(initLat) || initLng < -180 || initLng > 180 || initLat < -90 || initLat > 90) {
						initLng = defaultLngLat.lng
						initLat = defaultLngLat.lat
					}

					// 实例化地图
					const mapContainer = this.$refs.mapBoxRef
					if (!mapContainer) {
						throw new Error('地图容器不存在')
					}

					this.$map = new T.Map(mapContainer, {
						projection: 'EPSG:4326',
						center: new T.LngLat(initLng, initLat),
						zoom: 12,
					})

					if (!this.$map) {
						throw new Error('地图实例化失败')
					}

					// 修改图层加载代码
					// 添加图层
					const normalLayer = new T.TileLayer('vec', {
						key: this.tdtKey,
						opacity: 1,
						// 关键修复：设置空的错误瓦片URL
						errorTileUrl: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7', // 1x1透明GIF
						// 添加重试机制
						retryCount: 3,
						retryDelay: 1000,
						// 优化性能参数
						unloadInvisibleTiles: true,
						reuseTiles: true,
						// 添加瓦片加载错误回调
						tileError: function () {},
					})
					const annoLayer = new T.TileLayer('cva', {
						key: this.tdtKey,
						opacity: 1,
						errorTileUrl: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
						retryCount: 3,
						retryDelay: 1000,
						unloadInvisibleTiles: true,
						reuseTiles: true,
						tileError: function () {},
					})
					this.$map.addLayer(normalLayer)
					this.$map.addLayer(annoLayer)

					// 设置初始中心
					this.setMapCenter(initLng, initLat)

					// 触发初始定位事件
					this.togglePointChange({ lng: initLng, lat: initLat })

					// 初始化搜索服务 - 尝试更多可能的方式
					try {
						// 尝试多种可能的搜索服务构造函数
						const searchConstructors = [
							T.service?.Search,
							T.SearchService,
							T.Search,
							window.Tdta?.SearchService,
							window.Tdta?.Search,
							window.TianDiTu?.SearchService,
							null, // 最后使用REST API
						]

						let SearchConstructor = null
						for (const ctor of searchConstructors) {
							if (ctor && typeof ctor === 'function') {
								SearchConstructor = ctor
								break
							}
						}

						if (SearchConstructor) {
							// 使用找到的构造函数
							const Constructor: any = SearchConstructor
							this.searchService = new Constructor({
								key: this.tdtKey,
								pageSize: 10,
								pageNum: 1,
							})
							this.isSearchReady = true
							this.hasSearchError = false
						} else {
							// 未找到构造函数，使用REST API作为备选
							console.warn('未找到搜索服务构造函数，将使用REST API进行搜索')
							this.searchService = 'rest-api' // 标记使用REST API
							this.isSearchReady = true
							this.hasSearchError = false
							this.searchErrorMsg = '使用基础搜索功能'
						}
					} catch (err) {
						console.error('搜索服务初始化失败:', err)
						// 尝试使用REST API备选方案
						this.searchService = 'rest-api'
						this.isSearchReady = true
						this.hasSearchError = true
						this.searchErrorMsg = '高级搜索不可用，使用基础搜索'
					}

					// 初始化地理编码服务
					try {
						const geocoderConstructors = [T.service?.Geocoder, T.Geocoder, window.Tdta?.Geocoder, window.TianDiTu?.Geocoder]

						let GeocoderConstructor = null
						for (const ctor of geocoderConstructors) {
							if (ctor && typeof ctor === 'function') {
								GeocoderConstructor = ctor
								break
							}
						}

						if (GeocoderConstructor) {
							const Constructor: any = GeocoderConstructor
							this.geocoder = new Constructor({
								key: this.tdtKey,
							})
						} else {
							console.warn('未找到地理编码服务构造函数')
						}
					} catch (err) {
						console.error('地理编码服务初始化失败:', err)
					}

					// 创建标记点
					this.createMarker(initLng, initLat)

					// 地图点击事件
					this.$map.addEventListener('click', (e: any) => {
						if (e && e.lnglat) {
							const { lng, lat } = e.lnglat
							this.updateMarkerPosition(lng, lat)

							if (this.geocoder) {
								this.geocoder.getAddress(e.lnglat, (result: any) => {
									if (result.status === 0) {
										this.togglePointChange({
											lng,
											lat,
											address: result.result.address,
										})
									} else {
										this.togglePointChange({ lng, lat })
									}
								})
							} else {
								this.togglePointChange({ lng, lat })
							}
						}
					})

					// 新增：地图移动/缩放结束事件，更新可视范围
					this.$map.addEventListener('moveend', () => {
						this.updateVisibleBounds()
					})

					// 初始化时获取一次范围
					this.updateVisibleBounds()

					this.$emit('ready', this.$map)
					this.isLoading = false
				})
				.catch((err) => {
					console.error('地图初始化错误:', err)
					this.$emit('error', err)
					this.isLoading = false
					this.hasSearchError = true
					this.searchErrorMsg = '地图加载失败: ' + err.message
				})
		},

		// 创建标记点
		createMarker(lng: number, lat: number) {
			if (this.$marker && this.$map && typeof this.$map.removeOverLay === 'function') {
				this.$map.removeOverLay(this.$marker)
			}

			try {
				this.$marker = new window.T.Marker(new window.T.LngLat(lng, lat), {
					draggable: true,
				})

				if (this.$map && typeof this.$map.addOverLay === 'function') {
					this.$map.addOverLay(this.$marker)
				}

				this.$marker.addEventListener('dragend', (e: any) => {
					if (e && e.lnglat) {
						const { lng, lat } = e.lnglat
						if (this.geocoder) {
							this.geocoder.getAddress(e.lnglat, (result: any) => {
								if (result.status === 0) {
									this.togglePointChange({
										lng,
										lat,
										address: result.result.address,
									})
								} else {
									this.togglePointChange({ lng, lat })
								}
							})
						} else {
							this.togglePointChange({ lng, lat })
						}
					}
				})
			} catch (err) {
				console.error('创建标记点失败:', err)
				this.$marker = null
			}
		},

		// 更新标记位置
		updateMarkerPosition(lng: number, lat: number) {
			console.log('更新标记位置:', lng, lat)
			this.$nextTick(() => {
				this.$emit('update:lng', lng)
				this.$emit('update:lat', lat)
				this.$emit('change', { lng, lat })
			})
			if (!this.$map) {
				console.warn('地图实例不存在')
				return
			}

			const point = new window.T.LngLat(lng, lat)

			if (!this.$marker) {
				this.createMarker(lng, lat)
			} else {
				if (typeof this.$marker.setLngLat === 'function') {
					this.$marker.setLngLat(point)
				} else if (typeof this.$marker.setPosition === 'function') {
					this.$marker.setPosition(point)
				} else {
					console.error('标记点没有设置位置的方法')
					return
				}
			}

			this.setMapCenter(lng, lat)
		},

		// 处理搜索输入
		handleSearchInput(e: Event) {
			if (!this.isSearchReady || !this.searchService) {
				return
			}

			const value = (e.target as HTMLInputElement).value.trim()
			if (!value) {
				// 清除定时器（防止之前的定时器仍然触发）
				if (this.searchTimer) {
					clearTimeout(this.searchTimer)
					this.searchTimer = null
				}
				// 清空搜索结果
				this.searchResults = []
				// 重置错误状态
				this.hasSearchError = false
				this.searchErrorMsg = ''
				// 重置加载状态
				this.isLoading = false
				return
			}

			if (this.searchTimer) {
				clearTimeout(this.searchTimer)
				this.searchTimer = null
			}
			this.searchTimer = window.setTimeout(() => {
				this.isLoading = true
				this.hasSearchError = false
				this.searchErrorMsg = ''
				// 使用REST API进行搜索
				this.restApiSearch(value)
					.then((result: any) => {
						console.log('搜索结果:', result)
						if (result.pois.pois && result.pois.pois.length > 0) {
							this.searchResults = result.pois.pois.map((poi) => ({
								name: poi.name || '未知地点',
								address: poi.address || poi.location.describe || '未知地址',
								lng: poi.lonlat.split(',')[0], // 字符串类型
								lat: poi.lonlat.split(',')[1], // 字符串类型
								zoom: poi.zoom || 15,
							}))
							this.handleSearch = true
							console.log('搜索结果:', this.searchResults)
							this.$emit('search-results', this.searchResults)
						} else {
							// console.log('搜索到区域:', pois.area);
							if (result.pois.area) {
								console.log('搜索到区域:', result.pois.area)
								var areas = result.pois.area
								this.searchResults = [
									{
										name: areas.name || '未知地点',
										address: areas.name || '未知地址',
										lng: areas.lonlat.split(',')[0], // 字符串类型
										lat: areas.lonlat.split(',')[1], // 字符串类型
										zoom: areas.zoom || 10,
									},
								]
								this.handleSearch = true
								console.log('搜索结果:', this.searchResults)
								this.$emit('search-results', this.searchResults)
							}
						}
					})
					.catch((err) => {
						// 弹出搜索失败提示
						this.$message.error('当前可视范围没有搜索到相关地点 ')
						console.error('REST API搜索失败:', err)
						this.searchResults = []
						this.isLoading = false
						this.hasSearchError = true
						this.searchErrorMsg = '搜索失败: ' + err.message
					})
					.finally(() => {
						// 无论成功失败，都更新加载状态
						this.isLoading = false
					})
			}, 300)
			console.log('搜索定时器:', this.searchTimer)
		},

		// // 处理搜索提交
		// handleSearchSubmit() {
		// 	if (!this.isSearchReady || !this.searchService || this.searchResults.length === 0) {
		// 		return;
		// 	}
		// 	this.handleSelectSearchResult(this.searchResults[0]);
		// },

		// 处理选择搜索结果 - 增强跳转功能
		handleSelectSearchResult(item: { lng: number; lat: number; name: string; address: string; zoom?: number }) {
			console.log('选择搜索结果:', item)
			this.$nextTick(() => {
				this.$emit('update:lng', item.lng)
				this.$emit('update:lat', item.lat)
				this.$emit('change', item)
			})
			this.handleSearch = false
			this.searchInput = item.name

			// 1. 先移动到搜索位置中心点，但不立即缩放
			// 创建一个新方法来只执行移动操作，避免触发缩放
			if (this.$map && typeof this.$map.panTo === 'function') {
				const point = new window.T.LngLat(item.lng, item.lat)
				this.$map.panTo(point) // 只移动，不缩放
			} else if (this.$map && typeof this.$map.setCenter === 'function') {
				const point = new window.T.LngLat(item.lng, item.lat)
				this.$map.setCenter(point) // 只移动，不缩放
			}

			// 2. 更新标记位置（但不调用setMapCenter）
			this.updateMarkerPositionWithoutZoom(item.lng, item.lat)

			// 3. 延迟一小段时间后再设置缩放级别，确保先完全移动到中心点
			setTimeout(() => {
				if (item.zoom && this.$map && typeof this.$map.setZoom === 'function') {
					this.$map.setZoom(item.zoom)
				}
			}, 300) // 延迟时间可根据实际效果调整

			// 清空搜索框并隐藏下拉列表
			;(this.$refs.searchInputRef as HTMLInputElement).value = item.name
			this.searchResults = []

			// 触发自定义事件
			this.$emit('select', {
				lng: item.lng,
				lat: item.lat,
				name: item.name,
				address: item.address,
			})
			this.togglePointChange({
				lng: item.lng,
				lat: item.lat,
				name: item.name,
				address: item.address,
			})
		},

		// 新增：更新标记位置但不自动缩放地图
		updateMarkerPositionWithoutZoom(lng: number, lat: number) {
			console.log('更新标记位置但不缩放:', lng, lat)

			if (!this.$map) {
				console.warn('地图实例不存在')
				return
			}

			const point = new window.T.LngLat(lng, lat)

			if (!this.$marker) {
				this.createMarker(lng, lat)
			} else {
				if (typeof this.$marker.setLngLat === 'function') {
					this.$marker.setLngLat(point)
				} else if (typeof this.$marker.setPosition === 'function') {
					this.$marker.setPosition(point)
				} else {
					console.error('标记点没有设置位置的方法')
					return
				}
			}

			// 注意：这里不调用setMapCenter，避免触发缩放
		},

		// 触发点位置变化事件
		togglePointChange(point: { lng: number; lat: number; [key: string]: any }) {
			const lng = Number(point.lng)
			const lat = Number(point.lat)

			this.$emit('update:lng', lng)
			this.$emit('update:lat', lat)
			this.$emit('change', { ...point, lng, lat })
		},
	},
	created() {
		console.log('tbt-picker组件created执行')
		// 提前准备一些数据或配置
	},
	beforeMount() {
		console.log('tbt-picker组件beforeMount执行')
		// 可以在这里尝试初始化一些不需要DOM的内容
	},
	mounted() {
		console.log(1111111111)

		this.$nextTick(() => {
			this.initMap()
		})
	},
	beforeDestroy() {
		if (this.searchTimer) {
			clearTimeout(this.searchTimer)
		}

		if (this.$map) {
			this.$map.removeEventListener('click')
			this.$map.removeEventListener('moveend') // 移除事件监听
			this.$map = null
		}

		if (this.$marker) {
			this.$marker.removeEventListener('dragend')
			this.$marker = null
		}

		this.searchService = null
		this.geocoder = null
	},
})
